import { cn } from '../../lib/cn';
import { partyLabel, type Party } from '../../lib/enums';

/**
 * Party encoding for tables: a 3px full-height color band in a gutter cell
 * (costs zero horizontal text space) plus an inline legend that names each
 * color once above the table instead of 100x down the page.
 *
 * The Party type, PARTY_TONE map, and partyLabel() live in lib/enums.ts.
 */

const PARTY_RAIL: Record<Party, string> = {
  client: 'bg-party-client',
  opposing: 'bg-party-opposing',
  third_party: 'bg-party-third',
  joint: 'bg-party-joint',
};

export type { Party };

export interface PartyRailProps {
  party: Party | null | undefined;
  /** Display name of the producing party — announced to screen readers. */
  name?: string;
  className?: string;
}

/** 3px full-height color rail for a table row. Unknown party renders an empty spacer. */
export default function PartyRail({ party, name, className }: PartyRailProps) {
  if (!party) {
    return <span className={cn('block h-full w-[3px]', className)} aria-hidden="true" />;
  }
  const label = name || partyLabel(party);
  return (
    <span className={cn('block h-full w-[3px] rounded-r-sm', PARTY_RAIL[party], className)}>
      <span className="sr-only">Produced by {label}</span>
    </span>
  );
}

export { PartyRail };

export interface PartyLegendProps {
  /** Parties actually present in the table below, in display order. */
  parties: Party[];
  clientName?: string | null;
  opposingName?: string | null;
  className?: string;
}

/** Inline legend row: one colored dot + name per party present. */
export function PartyLegend({ parties, clientName, opposingName, className }: PartyLegendProps) {
  if (parties.length === 0) return null;
  return (
    <div className={cn('flex items-center gap-3 text-2xs text-ink-faint', className)}>
      <span className="t-eyebrow">Produced by</span>
      {parties.map((p) => (
        <span key={p} className="inline-flex items-center gap-1">
          <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', PARTY_RAIL[p])} />
          {partyLabel(p, clientName, opposingName)}
        </span>
      ))}
    </div>
  );
}
